import {
  Button, FormLayoutGroup, FormItem,
  Panel, PullToRefresh, Tabs, TabsItem, Div,
} from '@vkontakte/vkui';
import { Pages, Panels } from '../Consts';
import React, { useState, useEffect, useRef } from 'react';
import { useAppContext } from '../../AppContext';
import { AmountInputComponent } from './components/AmountInputComponent';
import { NameInputComponent } from './components/NameInputComponent';
import { CommentInputComponent } from './components/CommentInputComponent';
import { DateInputComponent } from './components/DateInputComponent';
import { MembersInputComponent } from './components/MembersInputComponent';
import { UserSelectComponent } from './components/UserSelectComponent';
import {
  DistributionTypeTabsComponent,
  DISTRIBUTION_TYPE_EQUALLY,
  DISTRIBUTION_TYPE_VALUE,
  valueFormatterDistributions,
} from './components/DistributionTypeTabsComponent';
import { AddJoinedMembersComponent } from './components/AddJoinedMembersComponent';
import { clearCache as eventInfoCacheClear } from '../Events/ShowEventPanel';
import { ErrorLoadComponent } from '../components/ErrorLoadComponent';
import { getResponseFromError } from '../../helpers/helpers';
import { MAX_PAYMENT_AMOUNT, MIN_AMOUNT } from '../../Consts';
import { ImageUploadComponent } from './components/ImageUploadComponent';
import { SelectImagesDeleteComponent } from '../components/SelectImagesDeleteComponent';
import { getRouteEventInfo } from '../../route';
import { PlatformPanelHeader } from '../components/PlatformPanelHeader';

const CURRENT_PANEL = Panels.CREATE_PAYMENT_PANEL;
const CreatePaymentPanel = (props) => {
  const context = useAppContext();
  const params = context.router.getParams();

  const [users, setUsers] = useState([])
  const [name, setName] = useState('');
  const [nameError, setNameError] = useState('');
  const [comment, setComment] = useState('');
  const [commentError, setCommentError] = useState('');
  const [price, setPrice] = useState('');
  const [priceError, setPriceError] = useState('');
  const [dateTime, setDateTime] = useState(context.lang().dateFormatToInput(new Date()));
  const [dateError, setDateError] = useState('');
  const [images, setImages] = useState([]);
  const [imagesError, setImagesError] = useState('');
  const [creatorId, setCreatorId] = useState(context.store.currentUserId);
  const [creatorError, setCreatorError] = useState('');
  const [members, setMembers] = useState([]);
  const [usersForSelectMembers, setUsersForSelectMembers] = useState([]);
  const [membersError, setMembersError] = useState('');

  const [distributionType, setDistributionType] = useState(DISTRIBUTION_TYPE_EQUALLY);
  const [distributionTypeError, setDistributionTypeError] = useState('');

  const [equallyDistributions, setEquallyDistributions] = useState({});
  const [equallyDistributionsErrors, setEquallyDistributionsErrors] = useState({});
  const [partDistributions, setPartDistributions] = useState({});
  const [partDistributionsErrors, setPartDistributionsErrors] = useState({});
  const [valueDistributions, setValueDistributions] = useState({});
  const [valueDistributionsErrors, setValueDistributionsErrors] = useState({});
  const [valueDistributionsEdited, setValueDistributionsEdited] = useState(false);
  const [addJoinedMembers, setAddJoinedMembers] = useState(true);

  const isFormSavedRef = useRef(false);
  const [sendSave, setSendSave] = useState(false);
  const [errorInit, setErrorInit] = useState(null);
  const [showFull, setShowFull] = useState(false);

  const fetch = (spinner = true) => {
    if (spinner) {
      context.spinner(true);
    }

    context.init()
      .then((data) => {
        setCreatorId(data.user_id);
        context.httpClient().getEventV3(params.eventId)
          .then((response) => {
            setUsers([...response.data.users]);
            context.spinner(false);

            const allUsers = response.data.users.map((user) => ({
              value: user.id,
              label: user.name,
              img: user.img,
              type: user.type,
            }));
            setUsersForSelectMembers(allUsers);
            setMembers(allUsers.filter((user) => user.value === context.store.currentUserId));

            setErrorInit(null);
          })
          .catch((error) => {
            setErrorInit(error);
          })
      })
      .catch((error) => {
        setErrorInit(error);
      });
  }

  useEffect(() => {
    fetch();
    setName(params.name || '');
    setComment(params.comment || '');
    setPrice(params.price || '');
    setDateTime(context.lang().dateFormatToInput(params.date || new Date()))
  }, []);

  const handleSaveButtonClick = () => {
    if (isFormSavedRef.current) {
      return;
    }

    isFormSavedRef.current = true;
    setSendSave(true);
    context.spinner(true);
    const distributionTypeValue = valueFormatterDistributions(distributionType, members.map(({ value }) => value), partDistributions, valueDistributions);
    context.httpClient()
      .addPaymentV4(
        params.eventId,
        name,
        comment,
        price,
        dateTime,
        distributionTypeValue,
        creatorId,
        distributionType,
        images.map((img) => img.id),
        addJoinedMembers,
        (params.receiptId || '')
      )
      .then((response) => {
        eventInfoCacheClear(params.eventId, context.cache);
        isFormSavedRef.current = false;
        setSendSave(false);
        context.spinner(false);
        if ((params.openPage || '') === 'events_list') {
          context.router.replace(...getRouteEventInfo(params.eventId));
        } else {
          context.router.backTo(Panels.EVENT_INFO)
            .then(() => {})
            .catch((r) => {
              r.replace(...getRouteEventInfo(params.eventId));
            });
        }
      })
      .catch((error) => {
        isFormSavedRef.current = false;
        setSendSave(false);
        context.spinner(false);
        const response = getResponseFromError(error);
        if (!response) {
          context.showError(error);
          return;
        }

        setNameError(response.name || '');
        delete response['name'];
        setCommentError(response.comment || '');
        delete response['comment'];
        setPriceError(response.amount || '');
        delete response['amount'];
        setDateError(response.date || '');
        delete response['date'];
        setCreatorError(response.creator || '');
        delete response['creator'];
        setDistributionTypeError(response.distribution_type || '');
        delete response['distribution_type'];

        if (response.values) {
          setMembersError(response.values || '');
          delete response['values'];
        }
        if (response.parts) {
          setMembersError(response.parts || '');
          delete response['parts'];
        }

        const valueTypeErrors = {};
        const partsTypeErrors = {};
        const equallyTypeErrors = {};
        const imagesErrors = [];
        Object.keys(response).forEach((key) => {
          const [name, index] = key.split('.', 2);
          switch (name) {
            case 'values':
              valueTypeErrors[index] = response[key];
              delete response[key];
              break;
            case 'parts':
              partsTypeErrors[index] = response[key];
              delete response[key];
              break;
            case 'users':
              equallyTypeErrors[index] = response[key];
              delete response[key];
              break;
            case 'images':
              imagesErrors.push(response[key]);
              delete response[key];
              break;
          }
        });
        setEquallyDistributionsErrors(equallyTypeErrors);
        setPartDistributionsErrors(partsTypeErrors);
        setValueDistributionsErrors(valueTypeErrors);
        setImagesError(imagesErrors.join(','));

        if (response.message) {
          context.showError(null, response.message);
        } else if (Object.keys(response).length > 0) {
          context.showError(error);
        }
      });

    context.getBridge().retargeting.createPayment(context.store.currentUserId);
  }

  if (errorInit) {
    return (<ErrorLoadComponent
      error={errorInit}
      onFetch={fetch}
    />)
  }

  return (<Panel id={CURRENT_PANEL}>
    <PlatformPanelHeader
      text={context.lang().getKey(CURRENT_PANEL, 'header')}
    />
    {context.snackbar}
    <form>
      <FormLayoutGroup mode="vertical">
        <PullToRefresh onRefresh={() => fetch(false)}>
          <NameInputComponent
            name={name}
            setName={setName}
            error={nameError}
            setError={setNameError}
            label={context.lang().getKey('app', 'name_input_label')}
            errorTextMinLength={context.lang().getKey('errors', 'name_error_min_length_2_char')}
          />
          <AmountInputComponent
            amount={price}
            setAmount={setPrice}
            min={MIN_AMOUNT}
            max={MAX_PAYMENT_AMOUNT}
            error={priceError}
            setError={setPriceError}
            label={context.lang().getKey('app', 'amount_input_label')}
            errorTextMin={context.lang().getKey('errors', 'amount_error_min')}
            errorTextMax={context.lang().getKey('errors', 'amount_error_max')}
          />
          <DateInputComponent
            defaultDate={context.lang().dateFormatToInput(new Date())}
            dateTime={dateTime}
            setDateTime={setDateTime}
            error={dateError}
            setError={setDateError}
            label={context.lang().getKey('app', 'date_input_label')}
            errorTextMinValue={context.lang().getKey('errors', 'date_error_min')}
            errorTextMaxValue={context.lang().getKey('errors', 'date_error_max')}
          />
          <MembersInputComponent
            error={membersError}
            setError={setMembersError}
            members={usersForSelectMembers}
            value={members}
            setValue={setMembers}
            label={context.lang().getKey('app', 'members_input_label')}
            placeholder={context.lang().getKey('app', 'members_input_placeholder')}
            allItemsLabel={context.lang().getKey('app', 'all_members_items_label')}
          />
          <UserSelectComponent
            label={context.lang().getKey('app', 'creator_input_label')}
            placeholder={context.lang().getKey('app', 'creator_input_placeholder')}
            error={creatorError}
            setError={setCreatorError}
            members={usersForSelectMembers}
            value={creatorId}
            setValue={setCreatorId}
          />
          <DistributionTypeTabsComponent
            price={price}
            creator={creatorId}
            members={members}
            distributionType={distributionType}
            setDistributionType={setDistributionType}
            equallyDistributionsValue={equallyDistributions}
            setEquallyDistributionsValue={setEquallyDistributions}
            partDistributionsValue={partDistributions}
            setPartDistributionsValue={setPartDistributions}
            partDistributionsErrors={setPartDistributionsErrors}
            valueDistributionsValue={valueDistributions}
            setValueDistributionsValue={setValueDistributions}
            valueDistributionsErrors={valueDistributionsErrors}
            setValueDistributionsErrors={setValueDistributionsErrors}
            labelTabEqually={context.lang().getKey('app', 'distribution_type_label_equally')}
            labelTabValue={context.lang().getKey('app', 'distribution_type_label_value')}
            labelTabParts={context.lang().getKey('app', 'distribution_type_label_parts')}
            placeholder={context.lang().getKey('app', 'distribution_type_tab_placeholder')}
          />
          <AddJoinedMembersComponent
            label={context.lang().getKey('app', 'add_joined_members_input_label')}
            checked={addJoinedMembers}
            onChange={(event) => setAddJoinedMembers(event.target.checked)}
            disabled={distributionType === DISTRIBUTION_TYPE_VALUE}
          />
          <ImageUploadComponent
            eventId={params.eventId}
            value={images}
            setValue={setImages}
            error={imagesError}
            setError={setImagesError}
            label={context.lang().getKey('app', 'image_input_label')}
            errorTextMaxSize={context.lang().getKey('errors', 'image_error_max_size')}
            errorTextType={context.lang().getKey('errors', 'image_error_type')}
            errorMaxCount={context.lang().getKey('errors', 'image_error_max_count')}
            buttonLabel={context.lang().getKey('app', 'image_button_label__empty')}
            buttonLabelSelect={context.lang().getKey('app', 'image_button_label__selected')}
            maxSize={context.store.maxFileSize}
          />
          <SelectImagesDeleteComponent
            value={images}
            setValue={setImages}
          />
          <CommentInputComponent
            comment={comment}
            setComment={setComment}
            error={commentError}
            setError={setCommentError}
            label={context.lang().getKey('app', 'comment_input_label')}
            errorTextMaxLength={context.lang().getKey('errors', 'comment_error_max_length_255_char')}
          />
        </PullToRefresh>
        <FormItem>
          <Button
            size={'l'}
            stretched
            loading={sendSave}
            disabled={sendSave || !price || price < MIN_AMOUNT || price > MAX_PAYMENT_AMOUNT || name.length < 2}
            onClick={handleSaveButtonClick}
          >
            {context.lang().getKey(CURRENT_PANEL, 'button_save_label')}
          </Button>
        </FormItem>
      </FormLayoutGroup>
</form>
  </Panel>);
}

export default CreatePaymentPanel;
